
import Numpad from '@/libs/common/components/Numpad.vue';
import { Component, VModel, Watch } from 'vue-property-decorator';
import Page from '@/Page.vue';
import KeyInput from '@/libs/common/components/KeyInput.vue';
import {
    LocationModel,
    PanelDashboardModel,
    PanelDashboardSortTypeEnum,
    SortOrder,
    StationModel,
    WorkplaceModel,
} from '@/libs/Api';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import { CrudAction } from '@/libs/core/+state/models/crud-action';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { authStore } from '@/libs/auth/+store/store';
import { AuthAction } from '@/libs/auth/models/auth-state';
import { stationsStore } from '@/libs/stations/+state/store';
import { locationsStore } from '@/libs/locations/+state/store';
import { panelDashboardsStore } from '@/libs/panel-dashboards/+state/store';

@Component({
    components: { KeyInput, Numpad },
})
export default class ActivateQuickLoginDialog extends Page {
    @VModel() dialog!: boolean;
    protected filterWorkplaceName = '';
    protected isSaving = false;
    protected activeLocationId: string | null = null;
    protected activeWorkplaces: string[] = [];
    protected activeStations: string[] = [];
    protected usePanelDashboard = false;
    protected selectedPanelId: string | null = null;

    @Watch('dialog')
    dialogChanged(v: boolean, p: boolean) {
        if (v && v != p) {
            this.activeWorkplaces = [];
            this.filterWorkplaceName = '';
        }
    }

    protected get isDisabled(): boolean {
        if (this.usePanelDashboard && !this.selectedPanelId) {
            return true;
        }
        return false;
    }

    public get filteredWorkplaceList(): WorkplaceModel[] {
        const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        const locationScopes = locations
            .filter((x) => x.locationParents!.some((y) => y.parentalLocationId == this.activeLocationId))
            .map((x) => x.id!);

        const workplaces: WorkplaceModel[] = workplacesStore.useGetter(CrudGetter.Data, 'ActivateQuickLogin') ?? [];
        if (!this.filterWorkplaceName) {
            if (locationScopes.length > 0) {
                return workplaces.filter(
                    (x) => locationScopes.contains(x.locationId!) || this.activeWorkplaces.contains(x.id!)
                );
            }
            return [...workplaces];
        }

        let items = [...workplaces].filter((x) => {
            if (!x.name) {
                return false;
            }
            if (this.activeWorkplaces.contains(x.id!)) {
                return true;
            }
            if (locationScopes.length > 0 && !locationScopes.contains(x.locationId!)) {
                return false;
            }

            const filterGroups = (this.filterWorkplaceName || '')
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '') // Remove accents
                .split(/[^a-z0-9]+/) // Split by non-alphanumeric characters
                .filter((group) => group.trim().length > 0); // Remove any empty groups

            const normalizedName = x
                .name!.toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '');

            return filterGroups.some((group) => {
                return normalizedName.includes(group);
            });
        });

        if (items.length > 0) {
            return items;
        }

        items = [...workplaces].filter((x) => {
            if (!x.name) {
                return false;
            }
            if (this.activeWorkplaces.contains(x.id!)) {
                return true;
            }
            if (locationScopes.length > 0 && !locationScopes.contains(x.locationId!)) {
                return false;
            }

            const filterGroups = (this.filterWorkplaceName || '')
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '') // Remove accents
                .split(/[^a-z0-9]+/) // Split by non-alphanumeric characters
                .filter((group) => group.trim().length > 0); // Remove any empty groups

            const normalizedName = x
                .name!.toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '');

            return filterGroups.some((group) => {
                return (
                    normalizedName.includes(group) ||
                    group.split('').some((char, i) => {
                        const typoCheckGroup = group.slice(0, i) + group.slice(i + 1);
                        return normalizedName.includes(typoCheckGroup);
                    }) ||
                    group.split('').some((char, i) => {
                        for (let charCode = 97; charCode <= 122; charCode++) {
                            // Check with other characters (a-z)
                            const replacedGroup = group.slice(0, i) + String.fromCharCode(charCode) + group.slice(i + 1);
                            if (normalizedName.includes(replacedGroup)) {
                                return true;
                            }
                        }
                        return false;
                    })
                );
            });
        });

        return items;
    }

    protected get stations(): StationModel[] {
        if (this.activeWorkplaces.length == 1) {
            const items: StationModel[] = stationsStore.useGetter(CrudGetter.Data) ?? [];
            return items.filter((x) => x.workplaceId == this.activeWorkplaces[0]);
        }
        return [];
    }

    protected get locations(): LocationModel[] {
        let sortedLocations: LocationModel[] = [];

        // Function to recursively fetch and sort locations based on parent-child relationships
        function sortLocationsByHierarchy(locations: LocationModel[], parentId: string | null): void {
            const filteredLocations = locations.filter((location) => location.parentalLocationId === parentId);
            filteredLocations.sort((a, b) => a.name!.localeCompare(b.name!)); // Sort by name

            for (const location of filteredLocations) {
                sortedLocations.push(location); // Add current location to the result
                sortLocationsByHierarchy(locations, location.id!); // Recurse for its children
            }
        }

        const allLocations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        sortLocationsByHierarchy(allLocations, null);

        return sortedLocations;
    }

    protected get panelDashboards(): PanelDashboardModel[] {
        return panelDashboardsStore.useGetter(CrudGetter.Data, 'ActivateQuickLogin') ?? [];
    }

    mounted() {
        this.getWorkplaces();
        this.getPanelDashboards();
    }

    protected onCloseClick() {
        this.$emit('input', false);
    }

    protected isSelected(workplaceId: string): boolean {
        return this.activeWorkplaces.includes(workplaceId);
    }

    protected isSelectedStation(stationId: string): boolean {
        return this.activeStations.includes(stationId);
    }

    protected onActivateLocationClick(locationId: string) {
        if (this.activeLocationId == locationId) {
            this.activeLocationId = null;
        } else {
            this.activeLocationId = locationId;
        }
        this.activeWorkplaces = [];
        this.activeStations = [];
    }

    protected onActivateWorkplaceClick(workplace: WorkplaceModel) {
        if (this.isSelected(workplace.id!)) {
            this.activeWorkplaces.splice(this.activeWorkplaces.indexOf(workplace.id!), 1);
        } else {
            this.activeWorkplaces.push(workplace.id!);
        }
        this.activeStations = [];
    }

    protected onActivateStationClick(station: StationModel) {
        if (this.isSelectedStation(station.id!)) {
            this.activeStations.splice(this.activeStations.indexOf(station.id!), 1);
        } else {
            this.activeStations.push(station.id!);
        }
    }

    protected onActivatePanelDashboardClick(panelDashboard: PanelDashboardModel) {
        if (this.selectedPanelId != panelDashboard.id) {
            this.selectedPanelId = panelDashboard.id!;
        } else {
            this.selectedPanelId = null;
        }
    }

    protected onActivateClick() {
        window.localStorage.ActiveWorkplace = JSON.stringify(this.activeWorkplaces);
        window.localStorage.ActiveStation = this.activeWorkplaces.length == 1 ? JSON.stringify(this.activeStations) : null;
        if (this.usePanelDashboard && this.selectedPanelId) {
            window.localStorage.ActivePanelDashboard = this.selectedPanelId;
        } else {
            window.localStorage.ActivePanelDashboard = null;
        }
        authStore.dispatch(AuthAction.SetSharedMode, true);
        this.onCloseClick();
    }

    private getWorkplaces(): void {
        workplacesStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<WorkplaceModel>(
                { field: 'name', order: SortOrder.Asc, index: 1 },
                undefined,
                undefined,
                false,
                'ActivateQuickLogin'
            )
        );
    }

    private getPanelDashboards(): void {
        panelDashboardsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<PanelDashboardModel>(
                { field: 'name', order: SortOrder.Asc, index: 1 },
                undefined,
                undefined,
                false,
                'ActivateQuickLogin'
            )
        );
    }
}
